import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import { Plus, Trash2, Pencil } from "lucide-react";
import TableContainer from "Common/TableContainer";
import DeleteModal from "Common/DeleteModal";
import Modal from "Common/Components/Modal";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import useCurrentRole from "Common/hooks/useCurrentRole";
import { canManageContacts } from "Common/constants/roles";
import { apiErrorMessage } from "Common/constants/apiErrors";

const BASE_URL = "https://portal-api.mahanpanel.com";

// ContactOut — پاسخ GET /contacts/
interface Contact {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  phone_number: string;
  national_code?: string | null;
  postal_code?: string | null;
  address?: string | null;
  created_at_jalali?: string | null;
}

const authHeaders = () => {
  const token =
    localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
  return { Authorization: `Bearer ${token}` };
};

/** ارقام فارسی/عربی به لاتین؛ سرور خودش هم می‌پذیرد ولی اعتبارسنجی فرم به لاتین نیاز دارد. */
const toEnglishDigits = (value: string): string =>
  String(value ?? "")
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));

/** اعتبارسنجی استاندارد کد ملی ایران (۱۰ رقمی با رقم کنترلی). */
const isValidNationalCode = (code?: string | null): boolean => {
  const value = toEnglishDigits(String(code ?? ""));
  if (!/^\d{10}$/.test(value)) return false;
  if (/^(\d)\1{9}$/.test(value)) return false;
  const check = Number(value[9]);
  let sum = 0;
  for (let i = 0; i < 9; i += 1) sum += Number(value[i]) * (10 - i);
  const remainder = sum % 11;
  return remainder < 2 ? check === remainder : check === 11 - remainder;
};

/**
 * شماره همراه ایران. سرور «۰۹…»، ارقام فارسی و پیش‌شماره «+98» را می‌پذیرد و همه را
 * به قالب «09…» برمی‌گرداند؛ همین سه حالت اینجا هم معتبر شمرده می‌شوند.
 */
const isValidIranMobile = (value?: string | null): boolean => {
  const digits = toEnglishDigits(String(value ?? "")).replace(/[\s-]/g, "");
  return /^(?:0|\+?98|0098)?9\d{9}$/.test(digits);
};

const Index = () => {
  const navigate = useNavigate();
  // نقش‌های ۰ تا ۴ امکان ثبت/ویرایش/حذف دارند؛ ۵ تا ۷ فقط فهرست و جزئیات را می‌بینند.
  const { roleId } = useCurrentRole();
  const mayManage = canManageContacts(roleId);

  const [data, setData] = useState<Contact[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [reload, setReload] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<"default" | "asc" | "desc">("default");
  const changeServerSort = useCallback((nextSortBy: string | undefined, nextSortOrder: "default" | "asc" | "desc") => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
  }, []);

  const [show, setShow] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [eventData, setEventData] = useState<Contact | null>(null);

  // ========== دریافت فهرست ==========
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}/contacts/`, {
          headers: authHeaders(),
          params: sortOrder === "default" ? { sort_order: "default" } : { sort_by: sortBy, sort_order: sortOrder },
        });
        const items: Contact[] = Array.isArray(res.data) ? res.data : res.data?.items || [];
        setData(items);
        setErrorMessage("");
      } catch (error: any) {
        console.error("Error fetching contacts:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        setErrorMessage(apiErrorMessage(error, "مشکلی در دریافت فهرست رابط‌ها رخ داده است."));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reload, navigate, sortBy, sortOrder]);

  const toggle = useCallback(() => {
    setShow((prev) => {
      if (prev) {
        setEditMode(false);
        setEditingId(null);
      }
      return !prev;
    });
  }, []);

  // ========== فرم ثبت/ویرایش ==========
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      first_name: "",
      last_name: "",
      phone_number: "",
      national_code: "",
      postal_code: "",
      address: "",
    },
    validationSchema: Yup.object({
      first_name: Yup.string()
        .trim()
        .max(100, "حداکثر ۱۰۰ نویسه")
        .required("لطفاً نام را وارد کنید"),
      last_name: Yup.string()
        .trim()
        .max(100, "حداکثر ۱۰۰ نویسه")
        .required("لطفاً نام خانوادگی را وارد کنید"),
      phone_number: Yup.string()
        .required("لطفاً شماره تماس را وارد کنید")
        .test("iran-mobile", "شماره همراه معتبر نیست (مثلاً ۰۹۱۲۱۲۳۴۵۶۷)", (value) =>
          isValidIranMobile(value)
        ),
      // سه فیلد زیر اختیاری‌اند؛ فقط اگر پر شوند اعتبارسنجی می‌شوند.
      national_code: Yup.string()
        .notRequired()
        .test("national-code", "کد ملی باید ۱۰ رقم و معتبر باشد", (value) =>
          !value ? true : isValidNationalCode(value)
        ),
      postal_code: Yup.string().notRequired().max(20, "حداکثر ۲۰ نویسه"),
      address: Yup.string().notRequired().max(1000, "حداکثر ۱۰۰۰ نویسه"),
    }),
    onSubmit: async (values) => {
      // فیلدهای اختیاری خالی با null ارسال می‌شوند، نه رشته‌ی خالی.
      const optional = (value: string) => {
        const trimmed = String(value ?? "").trim();
        return trimmed === "" ? null : trimmed;
      };
      const payload = {
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        // شماره تماس هرگز به عدد تبدیل نمی‌شود تا صفر ابتدایی از بین نرود.
        phone_number: toEnglishDigits(values.phone_number).trim(),
        national_code: optional(toEnglishDigits(values.national_code)),
        postal_code: optional(toEnglishDigits(values.postal_code)),
        address: optional(values.address),
      };

      try {
        const res =
          editMode && editingId
            ? await axios.patch(`${BASE_URL}/contacts/${editingId}`, payload, {
              headers: { ...authHeaders(), "Content-Type": "application/json" },
            })
            : await axios.post(`${BASE_URL}/contacts/`, payload, {
              headers: { ...authHeaders(), "Content-Type": "application/json" },
            });
        if (res.status >= 200 && res.status < 300) {
          Swal.fire(
            "عملیات موفق",
            editMode ? "اطلاعات رابط بروزرسانی شد" : "رابط جدید ثبت شد",
            "success"
          );
          setReload((prev) => !prev);
          toggle();
        }
      } catch (error: any) {
        console.error("Contact submit error:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        Swal.fire("خطا", apiErrorMessage(error, "مشکل در ثبت یا ویرایش رابط"), "error");
      }
    },
  });

  const handleEdit = (row: Contact) => {
    setEditMode(true);
    setEditingId(row.id);
    validation.setValues({
      first_name: row.first_name || "",
      last_name: row.last_name || "",
      phone_number: row.phone_number || "",
      national_code: row.national_code || "",
      postal_code: row.postal_code || "",
      address: row.address || "",
    });
    setShow(true);
  };

  // ========== حذف ==========
  const handleDelete = async () => {
    if (!eventData) return;
    try {
      const res = await axios.delete(`${BASE_URL}/contacts/${eventData.id}`, {
        headers: authHeaders(),
      });
      if (res.status >= 200 && res.status < 300) {
        Swal.fire("عملیات موفق", "رابط با موفقیت حذف شد", "success");
        setData((prev) => prev.filter((item) => item.id !== eventData.id));
      }
    } catch (error: any) {
      console.error("Contact delete error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      // ۴۰۹ یعنی این رابط در یک پیش‌فاکتور استفاده شده است؛ متن دقیق سرور نمایش داده می‌شود.
      Swal.fire("عملیات ناموفق", apiErrorMessage(error, "امکان حذف این رابط وجود ندارد"), "error");
    } finally {
      setDeleteModal(false);
      setEventData(null);
    }
  };

  const columns = useMemo(
    () => {
      const base: any[] = [
        { header: "نام", accessorKey: "first_name", meta: { serverSortBy: "full_name" } },
        { header: "نام خانوادگی", accessorKey: "last_name" },
        {
          header: "شماره تماس",
          accessorKey: "phone_number",
          cell: (cell: any) => cell.getValue() || "—",
        },
        {
          header: "کد ملی",
          accessorKey: "national_code",
          cell: (cell: any) => cell.getValue() || "—",
        },
        {
          header: "کد پستی",
          accessorKey: "postal_code",
          cell: (cell: any) => cell.getValue() || "—",
        },
        {
          header: "آدرس",
          accessorKey: "address",
          cell: (cell: any) => cell.getValue() || "—",
        },
      ];
      if (!mayManage) return base;
      return [
        ...base,
        {
          header: "عملیات",
          enableColumnFilter: false,
          exportable: false,
          cell: (cell: any) => {
            const row: Contact = cell.row.original;
            return (
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => handleEdit(row)}
                  className="text-yellow-500 hover:text-yellow-700"
                  title="ویرایش"
                >
                  <Pencil className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEventData(row);
                    setDeleteModal(true);
                  }}
                  className="text-red-500 hover:text-red-700"
                  title="حذف"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            );
          },
        },
      ];
    },
    // handleEdit با هر رندر ساخته می‌شود ولی رفتارش ثابت است، پس وابستگی لازم ندارد.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mayManage]
  );

  return (
    <React.Fragment>
      <BreadCrumb title="ثبت رابط" pageTitle="داده‌های اولیه" />
      <DeleteModal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        onDelete={handleDelete}
      />

      {errorMessage && <div className="mb-3 text-center text-red-500">{errorMessage}</div>}

      <div className="card">
        <div className="card-body">
          <div className="flex items-center gap-3 mb-4">
            <h6 className="text-15 grow">
              مجموع رابط‌ها (<b className="total-Employs">{data.length}</b>)
            </h6>
            {mayManage && (
              <div className="shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    validation.resetForm();
                    setEditMode(false);
                    setEditingId(null);
                    setShow(true);
                  }}
                  className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
                >
                  <span className="align-middle">ثبت رابط</span>{" "}
                  <Plus className="inline-block size-4" />
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="inline-block w-8 h-8 border-4 rounded-full animate-spin border-custom-500 border-t-transparent"></div>
            </div>
          ) : data.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns}
              data={data}
              serverSorting={{ sortBy, sortOrder, onChange: changeServerSort }}
              customPageSize={10}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80"
              PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
            />
          ) : (
            <LoadingCounter count={1} text="هیچ رابطی ثبت نشده است" />
          )}
        </div>
      </div>

      {/* ========== مودال ثبت/ویرایش ========== */}
      <Modal show={show} onHide={toggle} modal-center="true" dialogClassName="w-screen md:w-[40rem]">
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500"
        >
          <Modal.Title className="text-16">
            {editMode ? "ویرایش رابط" : "ثبت رابط جدید"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(100vh-220px)] p-4 overflow-y-auto">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              validation.handleSubmit();
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" dir="rtl">
              <div>
                <label htmlFor="first_name" className="block mb-1 text-base font-medium">
                  نام: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  autoComplete="off"
                  className="w-full form-input"
                  placeholder="نام"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.first_name}
                />
                {validation.touched.first_name && validation.errors.first_name && (
                  <p className="mt-1 text-sm text-red-400">{validation.errors.first_name}</p>
                )}
              </div>

              <div>
                <label htmlFor="last_name" className="block mb-1 text-base font-medium">
                  نام خانوادگی: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  autoComplete="off"
                  className="w-full form-input"
                  placeholder="نام خانوادگی"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.last_name}
                />
                {validation.touched.last_name && validation.errors.last_name && (
                  <p className="mt-1 text-sm text-red-400">{validation.errors.last_name}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone_number" className="block mb-1 text-base font-medium">
                  شماره تماس: <span className="text-red-500">*</span>
                </label>
                {/* type="text" عمدی است: با type="number" صفر ابتدایی حذف می‌شود. */}
                <input
                  type="text"
                  inputMode="tel"
                  id="phone_number"
                  name="phone_number"
                  autoComplete="off"
                  className="w-full form-input"
                  placeholder="09121234567"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.phone_number}
                />
                {validation.touched.phone_number && validation.errors.phone_number && (
                  <p className="mt-1 text-sm text-red-400">{validation.errors.phone_number}</p>
                )}
              </div>

              <div>
                <label htmlFor="national_code" className="block mb-1 text-base font-medium">
                  کد ملی:
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  id="national_code"
                  name="national_code"
                  autoComplete="off"
                  className="w-full form-input"
                  placeholder="اختیاری"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.national_code}
                />
                {validation.touched.national_code && validation.errors.national_code && (
                  <p className="mt-1 text-sm text-red-400">{validation.errors.national_code}</p>
                )}
              </div>

              <div>
                <label htmlFor="postal_code" className="block mb-1 text-base font-medium">
                  کد پستی:
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  id="postal_code"
                  name="postal_code"
                  autoComplete="off"
                  className="w-full form-input"
                  placeholder="اختیاری"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.postal_code}
                />
                {validation.touched.postal_code && validation.errors.postal_code && (
                  <p className="mt-1 text-sm text-red-400">{validation.errors.postal_code}</p>
                )}
              </div>

              <div className="md:col-span-2">
                <label htmlFor="address" className="block mb-1 text-base font-medium">
                  آدرس:
                </label>
                <textarea
                  id="address"
                  name="address"
                  rows={3}
                  className="w-full form-input"
                  placeholder="اختیاری"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.address}
                />
                {validation.touched.address && validation.errors.address && (
                  <p className="mt-1 text-sm text-red-400">{validation.errors.address}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={toggle}
                className="bg-white btn border-slate-300 text-slate-600 hover:bg-slate-100 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-100"
              >
                انصراف
              </button>
              <button
                type="submit"
                disabled={validation.isSubmitting}
                className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600 disabled:opacity-60"
              >
                {editMode ? "بروزرسانی" : "ثبت"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default Index;
